import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Session } from 'next-auth';
import { SessionProvider, useSession } from 'next-auth/react';
import { env } from 'lib/env';
import { EngageProvider } from 'components/globalContext/EngageProvider';
import { AuthState } from 'lib/auth/types';
import { useMemo } from 'react';

type AuthProps = {
  session?: Session;
  skipAuthCheck?: boolean;
};

export const AuthImp: React.FC = ({ children }) => {
  const { status, data: session } = useSession({ required: false });

  const authState = useMemo<AuthState>(() => {
    if (status === 'loading') {
      return { isReady: false };
    }
    if (status === 'unauthenticated' || !session?.user) {
      return { isReady: true, isAuthenticated: false };
    }
    return {
      isReady: true,
      isAuthenticated: true,
      name: session.user.name || '',
      email: session.user.email || '',
      claims: {
        authz_center: session.center_types ?? [],
        authz_site: session.authz_site ?? [],
      },
    };
  }, [session, status]);

  return <EngageProvider authState={authState}>{children}</EngageProvider>;
};

export const Auth: React.FC<AuthProps> = ({ children, session, skipAuthCheck }) => {
  const sitecoreContext = useSitecoreContext();
  const skipAuthentication = env.SKIP_AUTHENTICATION;
  if (skipAuthentication) {
    return <EngageProvider>{children}</EngageProvider>;
  }
  const pageState = sitecoreContext.sitecoreContext.pageState;
  // Don't use when in Sitecore or when authentication support is not enabled
  if (pageState === 'normal' && !skipAuthCheck) {
    return (
      <SessionProvider session={session}>
        <AuthImp>{children}</AuthImp>
      </SessionProvider>
    );
  }
  return <SessionProvider session={session}>{children}</SessionProvider>;
};
